import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { rateCardsApi } from '@/utils/api';
import type { RateCard, ResourceLevel } from '@/types';
import type { RootState } from '../index';

interface RateCardsState {
  entities: RateCard[];
  currentEntity: RateCard | null;
  currentYear: number;
  loading: boolean;
  error: string | null;
}

const initialState: RateCardsState = {
  entities: [],
  currentEntity: null,
  currentYear: new Date().getFullYear(),
  loading: false,
  error: null,
};

// Async thunks
export const fetchRateCards = createAsyncThunk(
  'rateCards/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await rateCardsApi.getAll();
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch rate cards');
      }
      return (response.data as { rateCards: RateCard[] })?.rateCards || [];
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch rate cards');
    }
  }
);

export const fetchRateCardsByYear = createAsyncThunk(
  'rateCards/fetchByYear',
  async (year: number, { rejectWithValue }) => {
    try {
      const response = await rateCardsApi.getByYear(year);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch rate cards by year');
      }
      return (response.data as { rateCards: RateCard[] })?.rateCards || [];
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch rate cards by year');
    }
  }
);

export const fetchRateCardById = createAsyncThunk(
  'rateCards/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await rateCardsApi.getById(id);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch rate card');
      }
      return (response.data as { rateCard: RateCard })?.rateCard || null;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch rate card');
    }
  }
);

export const createRateCard = createAsyncThunk(
  'rateCards/create',
  async (data: Partial<RateCard>, { rejectWithValue }) => {
    try {
      const response = await rateCardsApi.create(data);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to create rate card');
      }
      return (response.data as { rateCard: RateCard })?.rateCard || null;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to create rate card');
    }
  }
);

export const updateRateCard = createAsyncThunk(
  'rateCards/update',
  async ({ id, data }: { id: string; data: Partial<RateCard> }, { rejectWithValue }) => {
    try {
      const response = await rateCardsApi.update(id, data);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to update rate card');
      }
      return (response.data as { rateCard: RateCard })?.rateCard || null;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to update rate card');
    }
  }
);

export const deleteRateCard = createAsyncThunk(
  'rateCards/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await rateCardsApi.delete(id);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to delete rate card');
      }
      return id;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to delete rate card');
    }
  }
);

const rateCardsSlice = createSlice({
  name: 'rateCards',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentEntity: (state, action: PayloadAction<RateCard | null>) => {
      state.currentEntity = action.payload;
    },
    setCurrentYear: (state, action: PayloadAction<number>) => {
      state.currentYear = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchRateCards.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRateCards.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = action.payload;
      })
      .addCase(fetchRateCards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch by year
      .addCase(fetchRateCardsByYear.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRateCardsByYear.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = action.payload;
      })
      .addCase(fetchRateCardsByYear.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch by ID
      .addCase(fetchRateCardById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRateCardById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentEntity = action.payload;
      })
      .addCase(fetchRateCardById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create
      .addCase(createRateCard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRateCard.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.entities.unshift(action.payload);
        }
      })
      .addCase(createRateCard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update
      .addCase(updateRateCard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRateCard.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const index = state.entities.findIndex(rc => rc.id === action.payload!.id);
          if (index !== -1) {
            state.entities[index] = action.payload;
          }
          if (state.currentEntity?.id === action.payload.id) {
            state.currentEntity = action.payload;
          }
        }
      })
      .addCase(updateRateCard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete
      .addCase(deleteRateCard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRateCard.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = state.entities.filter(rc => rc.id !== action.payload);
        if (state.currentEntity?.id === action.payload) {
          state.currentEntity = null;
        }
      })
      .addCase(deleteRateCard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setCurrentEntity, setCurrentYear } = rateCardsSlice.actions;

// Selectors
export const selectAllRateCards = (state: RootState) => state.rateCards.entities;
export const selectRateCardsByYear = (state: RootState, year?: number) => {
  const targetYear = year ?? state.rateCards.currentYear;
  return state.rateCards.entities.filter(rc => rc.year === targetYear);
};
export const selectRateForLevel = (state: RootState, level: ResourceLevel, year?: number) => {
  const targetYear = year ?? state.rateCards.currentYear;
  return state.rateCards.entities.find(rc => rc.resourceLevel === level && rc.year === targetYear);
};
export const selectCurrentYear = (state: RootState) => state.rateCards.currentYear;
export const selectRateCardsLoading = (state: RootState) => state.rateCards.loading;
export const selectRateCardsError = (state: RootState) => state.rateCards.error;
export const selectCurrentRateCard = (state: RootState) => state.rateCards.currentEntity;

export default rateCardsSlice.reducer;
